import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Floating WhatsApp button.
 * - Appears on all pages EXCEPT /admin
 * - Pre-fills a contextual message based on current route
 * - Phone: 055-4425912 (Israeli) → international format 972554425912
 */

const PHONE_INTL = '972554425912'; // +972 55-442-5912 — default Gal&Blizovsky
const ZCHUTA_PHONE_INTL = '972527707224'; // +972 52-770-7224 — Zchuta investor line

// Per-route phone overrides (defaults to PHONE_INTL)
const ROUTE_PHONES = {
  '/zchuta': ZCHUTA_PHONE_INTL,
};

// Per-route pre-filled messages
const ROUTE_MESSAGES = {
  '/': 'שלום, הגעתי לאתר של Gal&Blizovsky ואשמח לשמוע איך תוכלו לעזור לעסק שלי לצמוח.',
  '/services': 'שלום, אני מתעניין בשירותי השיווק והדיגיטל של Gal&Blizovsky.',
  '/method': 'שלום, קראתי על השיטה שלכם ואשמח לשמוע איך היא יכולה להתאים לעסק שלי.',
  '/about': 'שלום, ראיתי את עמוד "מי אנחנו" ואשמח להכיר את הצוות ולשמוע פרטים.',
  '/results': 'שלום, ראיתי את מקרי הבוחן שלכם ואשמח לשמוע איך תוכלו לייצר תוצאות גם אצלי.',
  '/contact': 'שלום, אני רוצה לקבוע שיחת אבחון אסטרטגית עם Gal&Blizovsky.',
  '/landing': 'שלום, ראיתי את דף הנחיתה. אני בעל עסק פעיל ואשמח לשמוע איך השיטה שלכם יכולה לפרוץ לי את תקרת הזכוכית בדיגיטל.',
  '/investors': 'שלום, אני משקיע פוטנציאלי. ראיתי את ההצעה ל"נדל"ן דיגיטלי" ואשמח לשמוע פרטים נוספים על המודל.',
  '/ceo': 'שלום, אני מנכ״ל / סמנכ״ל בחברה. ראיתי את דף הנחיתה של GAL BLIZOVSKY ואשמח לתאם פגישת אסטרטגיה ואבחון מערכות.',
  '/experts': 'שלום, אני מומחה בעל שיטה. ראיתי את דף הנחיתה למומחים ואשמח לתאם שיחת אפיון לבניית אדריכלות עסקית.',
  '/zchuta': 'שלום, אני משקיע פוטנציאלי. ראיתי את מצגת המשקיעים של זכותא ואשמח לתאם שיחה לקבלת ה-Deck המלא.'
};

const DEFAULT_MESSAGE = 'שלום, אשמח לקבל פרטים נוספים על השירותים של Gal&Blizovsky.';

const WhatsAppButton = () => {
  const location = useLocation();
  const [mounted, setMounted] = useState(false);

  // Animate in after first paint
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 400);
    return () => clearTimeout(t);
  }, []);

  // Hide on /admin
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  const message = ROUTE_MESSAGES[location.pathname] || DEFAULT_MESSAGE;
  const phone = ROUTE_PHONES[location.pathname] || PHONE_INTL;
  const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

  return (
    <a
      href={waUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="פתח שיחה בוואטסאפ עם Gal&Blizovsky"
      className={`fixed bottom-5 left-[5.5rem] z-[55] flex items-center justify-center w-14 h-14 rounded-full shadow-xl border-2 border-cream transition-all duration-500 ${
        mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{ backgroundColor: '#25D366' }}
    >
      <svg
        viewBox="0 0 24 24"
        className="w-7 h-7"
        aria-hidden="true"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill="#ffffff"
          d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.74.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.78 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01zM12.04 20.15h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.18 8.18 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.7 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13s-.64.81-.79.97-.29.19-.54.06c-.25-.12-1.05-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43s.17-.25.25-.41c.08-.17.04-.31-.02-.43s-.56-1.34-.76-1.84c-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31s-.86.84-.86 2.05.88 2.38 1 2.54c.12.17 1.74 2.66 4.22 3.73.59.25 1.05.4 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28z"
        />
      </svg>
      <span className="sr-only">פתח שיחה בוואטסאפ</span>
    </a>
  );
};

export default WhatsAppButton;
